import { useEffect, useState } from 'react';
import { boardApi } from '@/features/project-board/api';
import { QaBriefSchedule } from '@/features/project-board/qa-schedule';
import type { DocumentContent, QaScheduleDraft } from '@/features/project-board/types';

const INPUT = 'rounded-md border border-line bg-surface px-2 py-1 text-[12px] disabled:opacity-60';
const BUTTON = 'rounded-md border border-line bg-surface px-2 py-1 text-[12px] disabled:opacity-60';

/**
 * 역기획서의 `## QA 일정`을 고친다.
 *
 * 저장하면 하네스 저장소의 문서에 바로 커밋되고, 서버가 그 문서·에픽 일정의 색인도 함께 맞춘다. 저장 뒤에는
 * 서버가 돌려준 **고친 본문**으로 문서를 다시 그린다 — 같은 본문을 다시 조회할 이유가 없다.
 *
 * 펼치기 전에는 단추 하나만 둔다 — 역기획서는 읽는 문서고 일정은 가끔 고친다(`BodyEditor`와 같은 원칙).
 */
export function QaScheduleEditor({
  doc,
  onSaved,
}: {
  doc: DocumentContent;
  onSaved: (text: string) => void;
}): React.JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<QaScheduleDraft>(() => QaBriefSchedule.draftFrom(doc.text));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // 다른 문서를 열면 그 문서의 일정으로 되돌린다 — 안 그러면 이전 문서의 입력이 남아 엉뚱한 문서에 저장된다.
  useEffect(() => {
    setDraft(QaBriefSchedule.draftFrom(doc.text));
    setEditing(false);
    setError(null);
    setSaved(false);
  }, [doc.source, doc.path]);

  const set = (field: keyof QaScheduleDraft) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setDraft((current) => ({ ...current, [field]: event.target.value }));

  const missing = QaBriefSchedule.problemOf(draft);

  const save = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const { text } = await boardApi.saveQaSchedule(doc.source, doc.path, draft);
      onSaved(text);
      setEditing(false);
      setSaved(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setEditing(true)} className={BUTTON}>
          QA 일정 입력
        </button>
        {saved && (
          <span className="text-[11.5px] text-muted">
            하네스 문서에 저장했다. QA 보드·달력에는 새로 고침하면 보인다.
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-md border border-line bg-canvas px-3 py-3">
      <div className="mb-2 text-[12px] font-semibold">QA 일정</div>
      <div className="grid grid-cols-[96px_1fr] items-center gap-x-3 gap-y-2 text-[12px]">
        <label className="text-muted">검증 기간</label>
        <div className="flex flex-wrap items-center gap-2">
          <input type="date" value={draft.reviewStart} onChange={set('reviewStart')} disabled={saving} className={INPUT} />
          <span className="text-muted">~</span>
          <input
            type="date"
            value={draft.reviewEnd}
            min={draft.reviewStart || undefined}
            onChange={set('reviewEnd')}
            disabled={saving}
            className={INPUT}
          />
          <span className="text-[11px] text-faint">종료일을 비우면 미정</span>
        </div>

        <label className="text-muted">배포 목표일</label>
        <div className="flex flex-wrap items-center gap-2">
          <input type="date" value={draft.releaseTarget} onChange={set('releaseTarget')} disabled={saving} className={INPUT} />
          {draft.releaseTarget === '' && (
            <input
              type="text"
              value={draft.releaseOwner}
              onChange={set('releaseOwner')}
              disabled={saving}
              placeholder="확인 담당 (선택, 예: PM)"
              className={`${INPUT} w-56`}
            />
          )}
        </div>

        <label className="text-muted">제약·메모</label>
        <input
          type="text"
          value={draft.note}
          onChange={set('note')}
          disabled={saving}
          placeholder="없으면 비워 둔다"
          className={INPUT}
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => void save()} disabled={saving || missing !== null} className={BUTTON}>
          {saving ? '저장 중…' : '저장'}
        </button>
        <button
          type="button"
          onClick={() => {
            setDraft(QaBriefSchedule.draftFrom(doc.text));
            setEditing(false);
            setError(null);
          }}
          disabled={saving}
          className={BUTTON}
        >
          취소
        </button>
        <span className="text-[11.5px] text-muted">
          {missing ?? '저장하면 하네스 저장소의 이 문서에 바로 커밋된다.'}
        </span>
      </div>
      {error && <div className="mt-1 text-[11px] text-rose-700">저장 실패 — {error}</div>}
    </div>
  );
}
